import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import useStyles from '../styles';
import CustomButton from '../../../components/General/CustomButton';
import apiClient from '../../../requests/api/apiClient';
import { FetchError } from '../../types';

const DeleteSpeciesModalContent = ({
  id,
  handleCloseModal,
  handleDeleteFunction,
  isLoadingDelete,
}: {
  id: string,
  handleCloseModal: VoidFunction,
  handleDeleteFunction: ({ id }: { id: string }) => void,
  isLoadingDelete: boolean,
}) => {
  const classes = useStyles();
  const [count, setCount] = useState<number | null>(null);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const { data } = await apiClient.species.speciesVarietiesCount(id);
        if (!cancelled) setCount(data.count ?? 0);
      } catch (err) {
        const { error } = err as FetchError;
        if (!cancelled) setLoadError(error?.message ?? 'No se pudo obtener el conteo');
      }
    };
    fetchCount();
    return () => { cancelled = true; };
  }, [id]);

  const renderMessage = () => {
    if (loadError) {
      return (
        <Typography color='error'>
          {loadError}
        </Typography>
      );
    }
    if (count === null) {
      return (
        <Box display='flex' alignItems='center' justifyContent='center' p={2}>
          <CircularProgress size='1.5rem' />
        </Box>
      );
    }
    if (count === 0) {
      return (
        <Typography>
          ¿Estás seguro de querer eliminar esta especie? Esta acción no se puede deshacer.
        </Typography>
      );
    }
    return (
      <Typography>
        Eliminar esta especie también eliminará <strong>{count}</strong>
        {' '}
        {count === 1 ? 'variedad asociada' : 'variedades asociadas'}.
        {' '}
        Esta acción no se puede deshacer.
      </Typography>
    );
  };

  return (
    <Box className={classes.upsertVarietyModalContainer}>
      <Box className={classes.upsertVarietyModalHeader}>
        <Typography
          variant="h3"
          color="textPrimary"
          className={classes.upsertVarietyModalHeaderTitle}
        >
          <span className={classes.activePageUnderlined}>
            Eliminar especie
          </span>
        </Typography>
      </Box>
      <Box mt={2} mb={3}>
        {renderMessage()}
      </Box>
      <Box className={classes.upsertVarietyModalButtonsContainer}>
        <Box width='100%' mr={2}>
          <CustomButton color='blue' buttonText='Cancelar' onClick={handleCloseModal} />
        </Box>
        <Box width='100%' ml={2}>
          <CustomButton
            color='red'
            buttonText='Eliminar'
            onClick={() => handleDeleteFunction({ id })}
            disabled={isLoadingDelete || count === null}
            setLoadingSpinnerWhenDisabled={true}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default DeleteSpeciesModalContent;
